// @ts-nocheck
import { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  ArrowLeft,
  Calendar,
  User,
  Tag,
  Building,
  Star,
  MessageSquare,
  Edit,
  Link,
  Save,
  X,
  ChevronDown,
  Lightbulb,
  Link2,
  Layers,
  MoreVertical
} from 'lucide-react';
import { supabase } from '@product-radar/shared';
import { useWorkspace, useRegionalSettings } from '@/contexts/context';
import { formatDateTime } from '@/lib/formatting';
import { toast } from 'sonner';
import { LinkFeedbackIdeaDialog } from './link-feedback-idea-dialog';
import { LinkProductAreasDialog } from './link-product-areas-dialog';
import { AuditLog } from './audit-log';

interface FeedbackDetailProps {
  feedbackId: string;
  onBack: () => void;
  onNavigateToIdea?: (ideaId: string) => void;
  onNavigateToClient?: (clientId: string) => void;
}

export function FeedbackDetail({ feedbackId, onBack, onNavigateToIdea, onNavigateToClient }: FeedbackDetailProps) {
  const { workspace, membership } = useWorkspace();
  const regionalSettings = useRegionalSettings();
  const [feedback, setFeedback] = useState<any>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [linkedIdeas, setLinkedIdeas] = useState<any[]>([]);
  const [productAreas, setProductAreas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [productAreasDialogOpen, setProductAreasDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    client_id: '',
    contributor: '',
    source: '',
    content: '',
    sentiment: 'neutral' as 'positive' | 'negative' | 'neutral',
    status: 'New',
    priority: 'Medium' as 'Low' | 'Medium' | 'High' | 'Critical',
    product_area_id: '',
  });

  useEffect(() => {
    if (workspace && feedbackId) {
      loadFeedback();
      loadClients();
    }
  }, [workspace, feedbackId]);

  const loadFeedback = async () => {
    if (!workspace) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('feedback')
        .select(`
          *,
          client:clients(id, name),
          product_area:product_areas!product_area_id(id, name),
          feedback_tags(tag:tags(name))
        `)
        .eq('id', feedbackId)
        .eq('workspace_id', workspace.id)
        .single();

      if (error) throw error;

      // Load linked ideas separately
      const { data: ideaLinks, error: ideaLinksError } = await supabase
        .from('feedback_ideas')
        .select(`
          idea:ideas(id, title, status, priority)
        `)
        .eq('feedback_id', feedbackId);

      if (ideaLinksError) throw ideaLinksError;

      // Load product areas (both structure items and products)
      const { data: productAreaData, error: productAreaError } = await supabase
        .from('feedback_product_mapping')
        .select(`
          product_item_id,
          product_id,
          product_item:product_structure_items(id, name, type),
          product:products(id, name, color)
        `)
        .eq('feedback_id', feedbackId);

      if (!productAreaError && productAreaData) {
        const areas = [
          ...productAreaData.filter((item: any) => item.product_item).map((item: any) => item.product_item),
          ...productAreaData.filter((item: any) => item.product).map((item: any) => ({ ...item.product, type: 'product' }))
        ];
        setProductAreas(areas);
      }

      setFeedback(data);
      setLinkedIdeas(ideaLinks?.map((link: any) => link.idea).filter(Boolean) || []);
      setEditForm({
        client_id: data.client?.id || '',
        contributor: data.contributor || '',
        source: data.source || '',
        content: data.content || '',
        sentiment: data.sentiment || 'neutral',
        status: data.status || 'New',
        priority: data.priority || 'Medium',
        product_area_id: data.product_area_id || '',
      });
    } catch (error: any) {
      console.error('Error loading feedback:', error);
      toast.error('Failed to load feedback details', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    if (!workspace) return;

    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('workspace_id', workspace.id);

      if (error) throw error;

      setClients(data);
    } catch (error: any) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients', {
        description: error.message
      });
    }
  };

  const loadProductAreas = async () => {
    if (!workspace) return;

    try {
      const { data, error } = await supabase
        .from('product_areas')
        .select('*')
        .eq('workspace_id', workspace.id);

      if (error) throw error;

      setProductAreas(data);
    } catch (error: any) {
      console.error('Error loading product areas:', error);
      toast.error('Failed to load product areas', {
        description: error.message
      });
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset form to original values
    if (feedback) {
      setEditForm({
        client_id: feedback.client?.id || '',
        contributor: feedback.contributor || '',
        source: feedback.source || '',
        content: feedback.content || '',
        sentiment: feedback.sentiment || 'neutral',
        status: feedback.status || 'New',
        priority: feedback.priority || 'Medium',
        product_area_id: feedback.product_area_id || '',
      });
    }
  };

  const handleSaveChanges = async () => {
    if (!feedback) return;

    try {
      const { error } = await supabase
        .from('feedback')
        .update({
          client_id: editForm.client_id,
          contributor: editForm.contributor,
          source: editForm.source,
          content: editForm.content,
          sentiment: editForm.sentiment,
          status: editForm.status,
          priority: editForm.priority,
          product_area_id: editForm.product_area_id || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', feedback.id);

      if (error) throw error;

      toast.success('Feedback updated successfully');
      setIsEditing(false);
      loadFeedback();
    } catch (error: any) {
      console.error('Error updating feedback:', error);
      toast.error('Failed to update feedback', {
        description: error.message
      });
    }
  };

  const getSentimentBadge = (sentiment: string) => {
    const styles = {
      'positive': 'bg-green-50 text-green-600 border-green-200',
      'negative': 'bg-red-50 text-red-600 border-red-200',
      'neutral': 'bg-blue-50 text-blue-600 border-blue-200'
    };
    return styles[sentiment as keyof typeof styles] || 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const getPriorityBadge = (priority: string) => {
    const styles = {
      'Low': 'bg-gray-50 text-gray-600 border-gray-200',
      'Medium': 'bg-yellow-50 text-yellow-600 border-yellow-200',
      'High': 'bg-orange-50 text-orange-600 border-orange-200',
      'Critical': 'bg-red-50 text-red-600 border-red-200'
    };
    return styles[priority as keyof typeof styles] || styles['Medium'];
  };

  const StarRating = ({ rating }: { rating?: number }) => {
    if (!rating) return <span className="text-sm text-muted-foreground">No rating</span>;
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star 
            key={star} 
            className={`h-4 w-4 ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
          />
        ))}
      </div>
    );
  };

  // Check if user has permission to edit
  const canEdit = membership?.role && ['admin', 'product_manager', 'contributor'].includes(membership.role);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-1">
            <Skeleton className="h-96" />
          </div>
          <div className="col-span-2">
            <Skeleton className="h-96" />
          </div>
        </div>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <p className="text-muted-foreground mb-4">Feedback not found</p>
        <Button onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Feedback
        </Button>
      </div>
    );
  }

  const tags = feedback.feedback_tags?.map((ft: any) => ft.tag?.name).filter(Boolean) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <div className="flex items-center space-x-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-9">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="h-8 w-px bg-border" />
          <div>
            <div className="flex items-center space-x-2">
              <h1 className="text-2xl">Feedback Details</h1>
              <span className="text-sm text-muted-foreground">· ID: {feedback.id.substring(0, 8)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveChanges}>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </>
          ) : (
            <>
              {canEdit && (
                <Button variant="outline" size="sm" onClick={handleEditClick}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Link2 className="h-4 w-4 mr-2" />
                    Link
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setLinkDialogOpen(true)}>
                    <Lightbulb className="h-4 w-4 mr-2" />
                    Link to Idea
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setProductAreasDialogOpen(true)}>
                    <Layers className="h-4 w-4 mr-2" />
                    Link to Product Areas
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>

      {/* Edit Mode Banner */}
      {isEditing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Edit className="h-4 w-4 text-blue-600" />
              <p className="text-sm text-blue-900">You are now editing this feedback. Make your changes and click "Save Changes" when done.</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-6">
        {/* Left column - Details */}
        <div className="col-span-1 space-y-6">
          <Card className={`p-6 ${isEditing ? 'border-blue-200 shadow-sm' : ''}`}>
            <h3 className="mb-4">Overview</h3>
            
            <div className="space-y-4">
              {/* Customer */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Customer</Label>
                {isEditing ? (
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Select value={editForm.client_id} onValueChange={(value: any) => setEditForm({ ...editForm, client_id: value })}>
                      <SelectTrigger className="border-blue-200 focus:border-blue-400">
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client: any) => (
                          <SelectItem key={client.id} value={client.id}>
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                              <span>{client.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2 py-2">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    {feedback.client && onNavigateToClient ? (
                      <button
                        onClick={() => onNavigateToClient(feedback.client.id)}
                        className="text-[#245F73] hover:underline"
                      >
                        {feedback.client.name}
                      </button>
                    ) : (
                      <span>{feedback.client?.name || feedback.customer || 'Unknown'}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Contributor */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Contributor</Label>
                {isEditing ? (
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input 
                      value={editForm.contributor}
                      onChange={(e) => setEditForm({ ...editForm, contributor: e.target.value })}
                      className="pl-9 border-blue-200 focus:border-blue-400"
                      placeholder="Contributor name"
                    />
                  </div>
                ) : (
                  <div className="flex items-center space-x-2 py-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>{feedback.contributor || 'Unknown'}</span>
                  </div>
                )}
              </div>

              {/* Source */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Source</Label>
                {isEditing ? (
                  <Input 
                    value={editForm.source}
                    onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}
                    className="border-blue-200 focus:border-blue-400"
                    placeholder="e.g., Support Ticket, Email, Survey"
                  />
                ) : (
                  <div className="py-1">
                    <Badge variant="outline">{feedback.source}</Badge>
                  </div>
                )}
              </div>

              {/* Sentiment */}
              {feedback.sentiment && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Sentiment</Label>
                  {isEditing ? (
                    <Select value={editForm.sentiment} onValueChange={(value: any) => setEditForm({ ...editForm, sentiment: value })}>
                      <SelectTrigger className="border-blue-200 focus:border-blue-400">
                        <SelectValue placeholder="Select sentiment" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="positive">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span>Positive</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="neutral">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            <span>Neutral</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="negative">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            <span>Negative</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="py-1">
                      <Badge className={`${getSentimentBadge(feedback.sentiment)} text-xs`}>
                        {feedback.sentiment.charAt(0).toUpperCase() + feedback.sentiment.slice(1)}
                      </Badge>
                    </div>
                  )}
                </div>
              )}

              {/* Status */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Status</Label>
                {isEditing ? (
                  <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                    <SelectTrigger className="border-blue-200 focus:border-blue-400">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="Reviewed">Reviewed</SelectItem>
                      <SelectItem value="Planned">Planned</SelectItem>
                      <SelectItem value="Archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="py-1">
                    <Badge variant={feedback.status === 'Reviewed' ? 'default' : 'secondary'}>
                      {feedback.status}
                    </Badge>
                  </div>
                )}
              </div>

              {/* Priority */}
              {feedback.priority && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Priority</Label>
                  {isEditing ? (
                    <Select value={editForm.priority} onValueChange={(value: any) => setEditForm({ ...editForm, priority: value })}>
                      <SelectTrigger className="border-blue-200 focus:border-blue-400">
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="py-1">
                      <Badge className={`${getPriorityBadge(feedback.priority)} text-xs`}>
                        {feedback.priority}
                      </Badge>
                    </div>
                  )}
                </div>
              )}

              {/* Created At */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Created At</Label>
                <div className="flex items-center space-x-2 py-1">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {formatDateTime(feedback.created_at, regionalSettings)}
                  </span>
                </div>
              </div>

              {/* Tags */}
              {tags.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Tags</Label>
                  <div className="flex flex-wrap gap-2 py-1">
                    {tags.map((tag: string) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        <Tag className="h-3 w-3 mr-1" />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Product Areas */}
          {!isEditing && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Layers className="h-4 w-4 text-muted-foreground" />
                  <h3>Product Areas</h3>
                </div>
                {canEdit && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setProductAreasDialogOpen(true)}
                  >
                    <Link2 className="h-3 w-3 mr-1" />
                    {productAreas.length > 0 ? 'Edit' : 'Add'}
                  </Button>
                )}
              </div>
              {productAreas.length > 0 ? (
                <div className="space-y-2">
                  {productAreas.map((area: any) => (
                    <div key={area.id} className="flex items-center justify-between p-2 bg-muted rounded hover:bg-muted/70 transition-colors">
                      <span className="text-sm">{area.name}</span>
                      <Badge variant="outline" className="text-xs">{area.type}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Layers className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No product areas linked</p>
                  {canEdit && (
                    <p className="text-xs mt-1">Click "Add" to link product areas</p>
                  )}
                </div>
              )}
            </Card>
          )}
        </div>

        {/* Right column - Content */}
        <div className="col-span-2 space-y-6">
          {/* Feedback Content */}
          <Card className={`p-6 ${isEditing ? 'border-blue-200 shadow-sm' : ''}`}>
            <h3 className="mb-4">Feedback Content</h3>
            {isEditing ? (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Content</Label>
                <Textarea 
                  rows={12}
                  value={editForm.content}
                  onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                  className="w-full border-blue-200 focus:border-blue-400 resize-none"
                  placeholder="Enter the feedback content..."
                />
                <p className="text-xs text-muted-foreground">
                  {editForm.content.length} characters
                </p>
              </div>
            ) : (
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                  {feedback.content}
                </p>
              </div>
            )}
          </Card>

          {/* Additional Information */}
          {!isEditing && (
            <Card className="p-6">
              <h3 className="mb-4">Additional Information</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted-foreground">Created by:</span>
                  <span>{feedback.created_by || 'System'}</span>
                </div>
                {feedback.updated_at && feedback.updated_at !== feedback.created_at && (
                  <div className="flex justify-between py-2 border-b border-border/50">
                    <span className="text-muted-foreground">Last updated:</span>
                    <span>{formatDateTime(feedback.updated_at, regionalSettings)}</span>
                  </div>
                )}
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Workspace ID:</span>
                  <span className="font-mono text-xs">{feedback.workspace_id.substring(0, 8)}</span>
                </div>
              </div>
            </Card>
          )}

          {/* Linked Ideas */}
          {!isEditing && linkedIdeas.length > 0 && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Lightbulb className="h-4 w-4 text-muted-foreground" />
                  <h3>Linked Ideas ({linkedIdeas.length})</h3>
                </div>
              </div>
              <div className="space-y-2">
                {linkedIdeas.map((idea: any) => (
                  <div key={idea.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex-1">
                      <p 
                        className="font-medium text-sm text-primary hover:underline cursor-pointer"
                        onClick={() => onNavigateToIdea && onNavigateToIdea(idea.id)}
                      >
                        {idea.title}
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {idea.status}
                        </Badge>
                        {idea.priority && (
                          <span className="text-xs text-muted-foreground">Priority: {idea.priority}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Audit Log */}
          <AuditLog
            recordId={feedbackId}
            tableName="feedback"
          />
        </div>
      </div>

      {/* Link Feedback to Idea Dialog */}
      <LinkFeedbackIdeaDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        mode="feedback-to-idea"
        sourceId={feedbackId}
        onSuccess={loadFeedback}
      />

      {/* Link Feedback to Product Areas Dialog */}
      <LinkProductAreasDialog
        open={productAreasDialogOpen}
        onOpenChange={setProductAreasDialogOpen}
        entityId={feedbackId}
        entityType="feedback"
        onSuccess={loadFeedback}
      />
    </div>
  );
}